import React from "react";
import { Link as RouterLink } from "react-router-dom";
import {
  Box,
  Chip,
  Paper,
  Table,
  TableBody,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  Theme,
  useTheme,
} from "@mui/material";
import AppTableHeaderCell from "../../../../../views/components/common/pages/AppTableHeaderCell";
import AppTableRowCell from "../../../../../views/components/common/pages/AppTableRowCell";
import { SortType } from "../../../../../models/SortType";
import { StockIssueRequest } from "../../../../../models/StockIssueRequest/StockIssueRequest";
import { formatDate } from "../../../../../views/components/common/functions/formatDate";
import CustomTypography from "../../../../../views/components/common/form/CustomTypography";

interface StockIssueRequestsTableProps {
  requests: StockIssueRequest[];
  limit: number;
  page_no: number;
  total_count: number;
  total_pages: number;
  sort_by?: string;
  sort_order?: string;
  onPageChange: (
    event: React.MouseEvent<HTMLButtonElement> | null,
    page: number,
  ) => void;
  onRowsPerPageChange: (limit: number) => void;
  onSortChange: (column: string, sortOrder: string) => void;
  onView: (id: number) => void;
}

const getUniqueOrderIds = (req: StockIssueRequest): number[] =>
  Array.from(
    new Set(
      req.order_products_mapping
        .map((m) => m.order_id)
        .filter((orderId): orderId is number => orderId != null),
    ),
  );

const getPrimaryOrderId = (req: StockIssueRequest): number | null => {
  if (req.primary_order_id) return req.primary_order_id;
  const orderIds = getUniqueOrderIds(req);
  return orderIds.length === 1 ? orderIds[0]! : null;
};

const formatOrderIds = (req: StockIssueRequest) => {
  const orderIds = getUniqueOrderIds(req);
  if (orderIds.length === 0) return "—";
  return orderIds.map((orderId) => `#${orderId}`).join(", ");
};
const computeScope = (req: StockIssueRequest): string => {
  const skus = new Set(
    req.order_products_mapping
      .map((m) => m.sku)
      .filter((s): s is string => s !== null && s !== undefined),
  );
  const orders = new Set(
    req.order_products_mapping
      .map((m) => m.order_id)
      .filter((o): o is number => o !== null && o !== undefined),
  );
  return `${skus.size} SKU${skus.size !== 1 ? "s" : ""} × ${orders.size} Order${orders.size !== 1 ? "s" : ""}`;
};

const computeUnits = (req: StockIssueRequest): number =>
  req.total_issued_quantity ??
  req.order_products_mapping.reduce((sum, m) => sum + (m.quantity ?? 0), 0);

const getStatusChip = (req: StockIssueRequest) => {
  if (req.cancelled === 1) {
    return (
      <Chip
        label="Cancelled"
        size="small"
        sx={{
          backgroundColor: "#FEF3C7",
          color: "#92400E",
          fontWeight: 600,
          fontSize: "0.7rem",
        }}
      />
    );
  }
  if (req.rejected === 1) {
    return (
      <Chip
        label="Rejected"
        size="small"
        sx={{
          backgroundColor: "#FEE2E2",
          color: "#991B1B",
          fontWeight: 600,
          fontSize: "0.7rem",
        }}
      />
    );
  }
  if (req.auto_approved === 1) {
    return (
      <Chip
        label="Auto Approved"
        size="small"
        sx={{
          backgroundColor: "#CCFBF1",
          color: "#0F766E",
          fontWeight: 600,
          fontSize: "0.7rem",
        }}
      />
    );
  }
  if (req.approved === 1) {
    return (
      <Chip
        label="Approved"
        size="small"
        sx={{
          backgroundColor: "#DCFCE7",
          color: "#166534",
          fontWeight: 600,
          fontSize: "0.7rem",
        }}
      />
    );
  }
  return (
    <Chip
      label="Pending"
      size="small"
      sx={{
        backgroundColor: "#EFF6FF",
        color: "#1D4ED8",
        fontWeight: 600,
        fontSize: "0.7rem",
      }}
    />
  );
};

const processStatusChipColor = (
  status?: string,
): { bg: string; color: string } => {
  switch (status) {
    case "return_pending":
      return { bg: "#FEF3C7", color: "#92400E" };
    case "returned":
      return { bg: "#DCFCE7", color: "#166534" };
    case "rejected":
    case "cancelled":
    case "shipment_cancelled":
      return { bg: "#FEE2E2", color: "#991B1B" };
    case "issued":
      return { bg: "#DBEAFE", color: "#1D4ED8" };
    default:
      return { bg: "#F3F4F6", color: "#374151" };
  }
};

const getProcessStatusChip = (req: StockIssueRequest) => {
  if (!req.process_status_label) return null;
  const colors = processStatusChipColor(req.process_status);
  return (
    <Chip
      label={req.process_status_label}
      size="small"
      sx={{
        backgroundColor: colors.bg,
        color: colors.color,
        fontWeight: 600,
        fontSize: "0.65rem",
        mt: 0.5,
      }}
    />
  );
};

const StockIssueRequestsTable: React.FC<StockIssueRequestsTableProps> = ({
  requests,
  page_no,
  limit,
  total_count,
  sort_by,
  sort_order,
  onPageChange,
  onRowsPerPageChange,
  onSortChange,
  onView,
}) => {
  const theme = useTheme<Theme>();

  const handleSortToggle = (column: string) => {
    const newOrder =
      sort_by === column && sort_order === "desc" ? "asc" : "desc";
    onSortChange(column, newOrder);
  };

  const sortType = sort_order === "asc" ? SortType.ASC : SortType.DESC;

  const handleChangePage = (_: unknown, newPage: number) => {
    onPageChange(null, newPage + 1);
  };

  const handleChangeRowsPerPage = (
    event: React.ChangeEvent<HTMLInputElement>,
  ) => {
    onPageChange(null, 1);
    onRowsPerPageChange(parseInt(event.target.value, 10));
  };

  return (
    <Paper
      sx={{
        marginTop: "0.6rem",
        borderRadius: "8px",
        boxShadow: "0 2px 8px rgba(0, 0, 0, 0.1)",
        overflow: "hidden",
      }}
    >
      <TableContainer sx={{ maxHeight: window.innerHeight - 400 }}>
        <Table stickyHeader>
          <TableHead>
            <TableRow
              sx={{
                backgroundColor: theme.palette.customColors?.lightGreyColor,
                "& th": { py: 1, height: 48 },
              }}
            >
              <AppTableHeaderCell
                sx={{
                  textAlign: "center",
                  fontSize: 12,
                  position: "sticky",
                  left: 0,
                  zIndex: 20,
                  boxShadow: "10px 0 20px -12px rgba(115,115,115,0.3)",
                  backgroundColor: theme.palette.customColors?.lightGreyColor,
                  minWidth: "100px",
                }}
                title="Request ID"
                fontSize="12px"
                sortColumn="id"
                sortBy={sort_by}
                sortDirection={sortType}
                onSortChange={() => handleSortToggle("id")}
              />
              <AppTableHeaderCell
                sx={{ textAlign: "center", fontSize: 12, minWidth: "130px" }}
                fontSize="12px"
              >
                Order ID
              </AppTableHeaderCell>
              <AppTableHeaderCell
                sx={{ textAlign: "center", fontSize: 12, minWidth: "110px" }}
                fontSize="12px"
              >
                Shipment
              </AppTableHeaderCell>

              <AppTableHeaderCell
                sx={{ textAlign: "center", fontSize: 12, minWidth: "160px" }}
                fontSize="12px"
              >
                Scope
              </AppTableHeaderCell>
              <AppTableHeaderCell
                sx={{ textAlign: "center", fontSize: 12, minWidth: "80px" }}
                fontSize="12px"
              >
                Units
              </AppTableHeaderCell>
              <AppTableHeaderCell
                sx={{ textAlign: "center", fontSize: 12, minWidth: "130px" }}
                fontSize="12px"
              >
                Status
              </AppTableHeaderCell>
              <AppTableHeaderCell
                sx={{ textAlign: "center", fontSize: 12, minWidth: "120px" }}
                fontSize="12px"
                title="Raised On"
                sortColumn="created_date"
                sortBy={sort_by}
                sortDirection={sortType}
                onSortChange={() => handleSortToggle("created_date")}
              />
              <AppTableHeaderCell
                sx={{ textAlign: "center", fontSize: 12, minWidth: "130px" }}
                fontSize="12px"
              >
                Raised By
              </AppTableHeaderCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {requests.map((req, index) => (
              <TableRow
                key={req.id}
                hover
                sx={{
                  "&:nth-of-type(odd)": {
                    backgroundColor: theme.palette.background.paper,
                  },
                  "&:nth-of-type(even)": {
                    backgroundColor: theme.palette.action.hover,
                  },
                  "& td": { fontSize: "12px" },
                }}
              >
                <AppTableRowCell
                  sx={{
                    textAlign: "center",
                    fontWeight: 600,
                    color: theme.palette.primary.main,
                    position: "sticky",
                    left: 0,
                    zIndex: 10,
                    boxShadow: "10px 0 20px -12px rgba(115,115,115,0.3)",
                    backgroundColor:
                      index % 2 === 0
                        ? theme.palette.background.paper
                        : theme.palette.action.hover,
                  }}
                >
                  <Box
                    component="button"
                    type="button"
                    onClick={() => onView(req.id)}
                    sx={{
                      background: "none",
                      border: "none",
                      p: 0,
                      m: 0,
                      color: "inherit",
                      font: "inherit",
                      fontWeight: "inherit",
                      cursor: "pointer",
                      textDecoration: "underline",
                      textUnderlineOffset: "2px",
                    }}
                  >
                    #{req.id}
                  </Box>
                </AppTableRowCell>
                <AppTableRowCell>
                  {(() => {
                    const primaryOrderId = getPrimaryOrderId(req);
                    if (primaryOrderId) {
                      return (
                        <RouterLink
                          to={`/orders/add-orders?orderId=${primaryOrderId}`}
                          style={{
                            color: theme.palette.primary.main,
                            fontWeight: 700,
                            textDecoration: "underline",
                          }}
                        >
                          #{primaryOrderId}
                        </RouterLink>
                      );
                    }
                    return formatOrderIds(req);
                  })()}
                </AppTableRowCell>
                <AppTableRowCell sx={{ textAlign: "center" }}>
                  {req.shipment_id ? (
                    <RouterLink
                      to={`/shipments?search_text_value=${req.shipment_id}`}
                      style={{
                        color: theme.palette.primary.main,
                        fontWeight: 700,
                        textDecoration: "underline",
                      }}
                    >
                      #{req.shipment_id}
                    </RouterLink>
                  ) : (
                    "—"
                  )}
                </AppTableRowCell>

                <AppTableRowCell>{computeScope(req)}</AppTableRowCell>
                <AppTableRowCell sx={{ fontWeight: 600 }}>
                  {computeUnits(req)}
                </AppTableRowCell>
                <AppTableRowCell sx={{ textAlign: "center" }}>
                  <Box sx={{ display: "flex", flexDirection: "column", alignItems: "center" }}>
                    {getStatusChip(req)}
                    {getProcessStatusChip(req)}
                  </Box>
                </AppTableRowCell>
                <AppTableRowCell>
                  {formatDate(req.created_date)}
                </AppTableRowCell>
                <AppTableRowCell>{req.created_by_admin_name || "-"}</AppTableRowCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      {requests.length === 0 && (
        <Box
          sx={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            textAlign: "center",
            p: 4,
            borderTop: "1px solid",
            borderColor: "divider",
            backgroundColor:
              theme.palette.customColors?.grayBackGroundColor ||
              theme.palette.action.hover,
          }}
        >
          <CustomTypography
            variant="heading6"
            fontWeight={600}
            color={theme.palette.customColors?.greyTextColor}
          >
            No stock issue requests match the current filters.
          </CustomTypography>
        </Box>
      )}

      {total_count > 0 && (
        <TablePagination
          rowsPerPageOptions={[30, 50, 100, 200]}
          component="div"
          count={total_count}
          rowsPerPage={limit}
          page={page_no - 1}
          onPageChange={handleChangePage}
          onRowsPerPageChange={handleChangeRowsPerPage}
          sx={{
            borderTop: "1px solid",
            borderColor: "divider",
            backgroundColor: theme.palette.background.paper,
            "& .MuiTablePagination-selectLabel, & .MuiTablePagination-displayedRows":
            {
              fontSize: "0.8rem",
              color: theme.palette.customColors?.greyTextColor,
            },
          }}
        />
      )}
    </Paper>
  );
};

export default StockIssueRequestsTable;
